import axios from 'axios';

const DIRECTUS_URL = 'http://localhost/cms';

class AuthService {
  constructor() {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  // Register a new user
  async register(userData) {
    try {
      // First, register the user without the avatar
      const response = await axios.post(`${DIRECTUS_URL}/users/register`, {
        email: userData.email,
        password: userData.password,
        first_name: userData.firstName,
        last_name: userData.lastName,
      });

      // Directus returns 204 for security, so we can't update the avatar here
      // User will need to set their avatar after login in their profile settings

      return {
        success: true,
        message: 'Registration successful! Please check your email to verify your account.',
      };
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Registration failed');
    }
  }

  // Login user
  async login(email, password) {
    try {
      const response = await axios.post(`${DIRECTUS_URL}/auth/login`, {
        email,
        password,
      });

      const { access_token, refresh_token, expires } = response.data.data;

      // Store tokens
      this.accessToken = access_token;
      this.refreshToken = refresh_token;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('token_expires', expires);

      return {
        success: true,
        token: access_token,
      };
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Invalid credentials');
    }
  }

  // Refresh access token
  async refreshAccessToken() {
    try {
      const response = await axios.post(`${DIRECTUS_URL}/auth/refresh`, {
        refresh_token: this.refreshToken,
      });

      const { access_token, refresh_token, expires } = response.data.data;

      this.accessToken = access_token;
      this.refreshToken = refresh_token;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('token_expires', expires);

      return access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      throw new Error('Session expired. Please login again.');
    }
  }

  // Logout user
  async logout() {
    try {
      if (this.refreshToken) {
        await axios.post(`${DIRECTUS_URL}/auth/logout`, {
          refresh_token: this.refreshToken,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens regardless
      this.accessToken = null;
      this.refreshToken = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expires');
      localStorage.removeItem('user');
    }
  }

  // Get current user info
  async getCurrentUser() {
    try {
      if (!this.accessToken) {
        return null;
      }

      const response = await axios.get(`${DIRECTUS_URL}/users/me`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });

      const user = response.data.data;
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      if (error.response?.status === 401) {
        // Try to refresh token
        try {
          await this.refreshAccessToken();
          return this.getCurrentUser();
        } catch (refreshError) {
          this.logout();
          return null;
        }
      }
      console.error('Get user error:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.accessToken;
  }

  // Get access token
  getAccessToken() {
    // Always read from localStorage to get the latest token
    this.accessToken = localStorage.getItem('access_token');
    return this.accessToken;
  }

  // Force token refresh
  async forceRefresh() {
    if (!this.refreshToken) {
      this.refreshToken = localStorage.getItem('refresh_token');
    }
    return await this.refreshAccessToken();
  }

  // Get user from local storage
  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
}

export default new AuthService();
